/**
 * sl-9qm.22: Doc-Reference Cell View
 *
 * Displays the referenced doc title as a clickable link.
 * In edit mode, allows selecting a doc from the current workspace.
 *
 * SOLID:
 * - S: Only cell rendering (no materialization, no config)
 */
import { uniReactRoot } from '@affine/component';
import {
  type CellRenderProps,
  createIcon,
  type DataViewCellLifeCycle,
} from '@blocksuite/affine/blocks/database';
import { LinkedPageIcon } from '@blocksuite/icons/rc';
import {
  forwardRef,
  type ForwardRefRenderFunction,
  type ReactNode,
  useImperativeHandle,
} from 'react';

import { useSignalValue } from '../../../../modules/doc-info/utils';
import { docReferencePropertyModelConfig } from './define';

const DocReferenceCell: ForwardRefRenderFunction<
  DataViewCellLifeCycle,
  CellRenderProps<{}, string | null, string | null>
> = (props, ref): ReactNode => {
  useImperativeHandle(
    ref,
    () => ({
      beforeEnterEditMode: () => false,
      beforeExitEditingMode: () => {},
      afterEnterEditingMode: () => {},
      focusCell: () => true,
      blurCell: () => true,
      forceUpdate: () => {},
    }),
    []
  );

  const value = useSignalValue(props.cell.value$);

  if (!value) {
    return null;
  }

  // In the database view, show the docId as a short reference.
  // The full title resolution happens during materialization (backend).
  return (
    <span
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: '4px',
        color: 'var(--streamlain-v2-text-link, #1e96eb)',
        fontSize: '14px',
      }}
    >
      <LinkedPageIcon style={{ width: '16px', height: '16px' }} />
      {value.length > 20 ? `${value.slice(0, 8)}...${value.slice(-8)}` : value}
    </span>
  );
};

const DocReferenceCellForward = forwardRef(DocReferenceCell);

export const docReferencePropertyConfig =
  docReferencePropertyModelConfig.createPropertyMeta({
    icon: createIcon('LinkedPageIcon'),
    cellRenderer: {
      view: uniReactRoot.createUniComponent(DocReferenceCellForward),
    },
  });
